import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import axios from "axios";
import toast, { Toaster } from "react-hot-toast";

export default function AddEditCoupon() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [form, setForm] = useState({
    couponCode: "",
    minimumOrderValue: "",
    category: "",
    discountPercent: "",
    expiryDate: "",
    redemptionLimit: "",
  });

  const [categories, setCategories] = useState([]);

  useEffect(() => {
    // Fetch categories
    axios
      .get("https://api.livestockify.com/api/admin/product-category")
      .then((res) => setCategories(res.data))
      .catch(console.error);

    // Prefill coupon in edit mode
    if (isEdit) {
      axios
        .get(`https://api.livestockify.com/api/admin/coupons/${id}`)
        .then((res) => {
          const c = res.data;

          setForm({
            couponCode: c.couponCode || "",
            minimumOrderValue: c.minimumOrderValue || "",
            category: c.category || "",
            discountPercent: c.discountPercent || "",
            expiryDate: c.expiryDate ? c.expiryDate.split("T")[0] : "",
            redemptionLimit: c.redemptionLimit || "",
          });
        })
        .catch(console.error);
    }
  }, [id, isEdit]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const selectedDate = new Date(form.expiryDate);

    if (selectedDate < today) {
      return toast.error("Please enter a valid future date");
    }
    if (!form.couponCode) return toast.error("Coupon Code is required");
    if (!form.minimumOrderValue)
      return toast.error("Minimum Order Value is required");
    if (!form.category) return toast.error("Category is required");
    if (!form.discountPercent)
      return toast.error("Discount Percent is required");
    if (!form.expiryDate) return toast.error("Expiration Date is required");
    if (!form.redemptionLimit)
      return toast.error("Redemption Limit is required");

    const isExpired = new Date(form.expiryDate) < new Date();

    try {
      const payload = {
        ...form,
        expired: isExpired, // backend can use this flag
      };

      if (isEdit) {
        await axios.put(
          `https://api.livestockify.com/api/admin/coupons/${id}`,
          payload,
        );
      } else {
        await axios.post(
          `https://api.livestockify.com/api/admin/coupons`,
          payload,
        );
      }

      toast.success(isEdit ? "Coupon updated" : "Coupon added");
      navigate("/coupons");
    } catch (err) {
      console.error("Coupon save failed", err);
      toast.error("Failed to save coupon");
    }
  };

  return (
    <div className="flex min-h-screen  bg-gray-100">
      <Toaster position="top-right" />
      <Sidebar />

      <div className="flex-1 bg-white rounded-l-2xl p-4 md:p-8 pl-14 md:pl-8">
        <h1 className="text-lg font-semibold text-green-700 mb-8">
          {isEdit ? "Edit Coupon" : "Add Coupon"}
        </h1>

        <div className="flex justify-center">
          <div className="w-full max-w-3xl">
            <div className="max-w-3xl  space-y-6 mt-6">
              {/* Coupon Code */}

              <div className="grid grid-cols-1 md:grid-cols-[200px_1fr] items-center gap-3 md:gap-6 mb-5">
                <label className="text-sm font-medium text-gray-700 text-center md:text-right">
                  Coupon Code
                </label>

                <input
                  name="couponCode"
                  value={form.couponCode}
                  onChange={handleChange}
                  className="w-full max-w-[420px] border rounded-md px-3 py-2 text-sm"
                />
              </div>

              {/* Minimum Order */}
              <div className="grid grid-cols-1 md:grid-cols-[200px_1fr] items-center gap-3 md:gap-6 mb-5">
                <label className="text-sm font-medium text-gray-700 text-center md:text-right">
                  Minimum Order Value
                </label>

                <div className="relative max-w-[420px]">
                  {!form.minimumOrderValue && (
                    <span className="absolute left-3 top-2.5 text-gray-500 text-sm">
                      ₹
                    </span>
                  )}

                  <input
                    name="minimumOrderValue"
                    value={form.minimumOrderValue}
                    onChange={handleChange}
                    className="w-full max-w-[420px] border rounded-md px-3 py-2 text-sm"
                  />
                </div>
              </div>

              {/* Category */}

              <div className="grid grid-cols-1 md:grid-cols-[200px_1fr] items-center gap-3 md:gap-6 mb-5">
                <label className="text-sm font-medium text-gray-700 text-center md:text-right">
                  Category
                </label>

                <select
                  name="category"
                  value={form.category}
                  onChange={handleChange}
                  className="w-full max-w-[420px] border rounded-md px-3 py-2 text-sm
                 focus:outline-none focus:ring-1 focus:ring-gray-700"
                >
                  <option value="">Select</option>

                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.category}>
                      {cat.category}
                    </option>
                  ))}
                </select>
              </div>

              {/* Discount */}

              <div className="grid grid-cols-1 md:grid-cols-[200px_1fr] items-center gap-3 md:gap-6 mb-5">
                <label className="text-sm font-medium text-gray-700 text-center md:text-right">
                  Discount (%)
                </label>

                <div className="relative max-w-[420px]">
                  <input
                    name="discountPercent"
                    value={form.discountPercent}
                    onChange={handleChange}
                    className="w-full max-w-[420px] border rounded-md px-3 py-2 text-sm"
                  />
                  <span className="absolute right-3 top-2.5 text-gray-500 text-sm">
                    %
                  </span>
                </div>
              </div>

              {/* Expiry */}

              <div className="grid grid-cols-1 md:grid-cols-[200px_1fr] items-center gap-3 md:gap-6 mb-5">
                <label className="text-sm font-medium text-gray-700 text-center md:text-right">
                  Expiration Date
                </label>

                <input
                  type="date"
                  name="expiryDate"
                  value={form.expiryDate}
                  onChange={handleChange}
                  className="w-full max-w-[420px] border rounded-md px-3 py-2 text-sm"
                />
              </div>

              {/* Limit */}

              <div className="grid grid-cols-1 md:grid-cols-[200px_1fr] items-center gap-3 md:gap-6 mb-5">
                <label className="text-sm font-medium text-gray-700 text-center md:text-right">
                  Redemption Limit
                </label>
                <input
                  name="redemptionLimit"
                  value={form.redemptionLimit}
                  onChange={handleChange}
                  className="w-full max-w-[420px] border rounded-md px-3 py-2 text-sm"
                />

                {/* ACTIONS */}
                <div className="grid grid-cols-1 md:grid-cols-[200px_1fr] mt-6  md:ml-60 ml-0">
                  <div /> {/* empty label column */}
                  <div className="flex justify-center gap-4 w-full pr-20">
                    <button
                      onClick={() => navigate("/coupons")}
                      className="border px-6 py-2 rounded-md  text-sm"
                    >
                      Cancel
                    </button>

                    <button
                      onClick={handleSubmit}
                      className="bg-green-600 text-white px-6 py-2 rounded-md text-sm"
                    >
                      {isEdit ? "Update" : "Add"}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* 🔹 REUSABLE FIELD */
function Field({ label, children }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-[220px_1fr] items-center gap-y-2 gap-x-6">
      <label className="text-sm font-medium text-gray-700">{label}</label>
      <div className="max-w-[420px]">{children}</div>
    </div>
  );
}
